<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData

type EstateSummaryMetric = 'rent' | 'loan'

type CityEstateSummary = {
  city: string
  units: number
  rent: number
  loan: number
}

const props = withDefaults(
  defineProps<{
    estateObjects: EstateObjectData[]
    maxVisibleCities?: number
  }>(),
  {
    maxVisibleCities: 3,
  },
)

const { n, t } = useI18n()

const metrics = computed<{ key: EstateSummaryMetric; label: string; colorClass: string }[]>(() => [
  {
    key: 'rent',
    label: t('summary.rent'),
    colorClass: 'bg-success-400',
  },
  {
    key: 'loan',
    label: t('summary.loan'),
    colorClass: 'bg-action',
  },
])

const summariesByCity = computed<CityEstateSummary[]>(() => {
  const groupedEstateObjects = new Map<string, CityEstateSummary>()

  props.estateObjects.forEach((estateObject) => {
    const city = estateObject.city || t('summary.unknown_city')
    const citySummary = groupedEstateObjects.get(city) ?? {
      city,
      units: 0,
      rent: 0,
      loan: 0,
    }

    citySummary.units += toNumber(estateObject.number_of_units)
    citySummary.rent += toNumber(estateObject.actual_rent) * 12
    citySummary.loan += totalLoanAmount(estateObject)

    groupedEstateObjects.set(city, citySummary)
  })

  return [...groupedEstateObjects.values()].sort((left, right) => right.rent - left.rent)
})

const visibleSummaries = computed(() => summariesByCity.value.slice(0, props.maxVisibleCities))
const hiddenCityCount = computed(() => Math.max(summariesByCity.value.length - visibleSummaries.value.length, 0))

const maximums = computed<Record<EstateSummaryMetric, number>>(() =>
  visibleSummaries.value.reduce(
    (accumulator, summary) => ({
      rent: Math.max(accumulator.rent, summary.rent),
      loan: Math.max(accumulator.loan, summary.loan),
    }),
    { rent: 0, loan: 0 },
  ),
)

function metricBarWidth(summary: CityEstateSummary, metric: EstateSummaryMetric) {
  const maximum = maximums.value[metric]
  const value = summary[metric]

  if (maximum <= 0 || value <= 0) {
    return '0%'
  }

  return `${Math.max((value / maximum) * 100, 4)}%`
}

function totalLoanAmount(estateObject: EstateObjectData) {
  return estateObject.loans?.reduce((sum, loan) => sum + toNumber(loan.principal_amount), 0) ?? 0
}

function toNumber(value: unknown) {
  const numericValue = Number(value ?? 0)

  return Number.isFinite(numericValue) ? numericValue : 0
}
</script>

<template>
  <div v-if="summariesByCity.length" class="flex flex-col gap-6">
    <div v-for="summary in visibleSummaries" :key="summary.city" class="flex flex-col gap-3">
      <div>
        <h3 class="text-surface-950 font-semibold">{{ summary.city }}</h3>
        <p class="text-surface-500 text-sm">
          {{ t('summary.units', { count: summary.units }) }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <div v-for="metric in metrics" :key="metric.key" class="grid grid-cols-[5.5rem_1fr_auto] items-center gap-3">
          <span class="text-surface-500 text-sm">{{ metric.label }}</span>
          <div class="bg-surface-100 h-3 overflow-hidden rounded-full">
            <div
              class="h-full rounded-full"
              :class="metric.colorClass"
              :style="{ width: metricBarWidth(summary, metric.key) }" />
          </div>
          <span class="text-surface-950 min-w-24 text-right text-sm font-semibold">
            {{ n(summary[metric.key], 'euros') }}
          </span>
        </div>
      </div>
    </div>

    <p v-if="hiddenCityCount" class="text-surface-500 text-sm font-medium">
      {{ t('summary.more_cities', { count: hiddenCityCount }) }}
    </p>
  </div>

  <p v-else class="text-surface-500 text-sm">
    {{ t('summary.empty') }}
  </p>
</template>

<i18n>
{
  "de": {
    "summary": {
      "rent": "Miete",
      "loan": "Darlehen",
      "unknown_city": "Unbekannter Ort",
      "units": "{count} Einheit | {count} Einheiten",
      "more_cities": "+ {count} weitere Stadt | + {count} weitere Städte",
      "empty": "Keine Bestandsimmobilien vorhanden"
    }
  },
  "en": {
    "summary": {
      "rent": "Rent",
      "loan": "Loan",
      "unknown_city": "Unknown city",
      "units": "{count} unit | {count} units",
      "more_cities": "+ {count} more city | + {count} more cities",
      "empty": "No existing properties available"
    }
  }
}
</i18n>
